import React, { useState, useRef } from 'react';
import { ScrollView, ActivityIndicator } from 'react-native';
import styled from '@emotion/native';
import { H3, BodyText } from '@/shared/components/StyledText';
import { green, sand, mintGreen } from '@/shared/styles/colors';
import BackToTopButton from '@/shared/components/BackToTopButton';
import { useGetUser } from '@/shared/hooks/useGetUser';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import ConfirmDeleteAccountModal from '@/components/settings/ConfirmDeleteAccountModal';
import LoginDetailsSection from '@/components/settings/LoginDetailsSection';
import NotificationsSection from '@/components/settings/NotificationsSection';
import PaymentSection from '@/components/settings/PaymentSection';
import DeleteAccountSection from '@/components/settings/DeleteAccountSection';

const Settings = () => {
  const scrollViewRef = useRef<ScrollView>(null);
  const { user, isLoadingUser } = useGetUser();
  const isDesktop = useIsDesktop();

  const [isConfirmDeleteAccountModalOpen, setIsConfirmDeleteAccountModalOpen] =
    useState(false);

  const scrollToTop = () => {
    scrollViewRef.current?.scrollTo({ y: 0, animated: true });
  };

  const handleDeleteAccount = () => {
    setIsConfirmDeleteAccountModalOpen(true);
  };

  if (isLoadingUser) {
    return (
      <SettingsContainer isDesktop={isDesktop}>
        <LoadingContainer>
          <ActivityIndicator size="large" color={green} />
          <LoadingText>Loading settings...</LoadingText>
        </LoadingContainer>
      </SettingsContainer>
    );
  }

  return (
    <SettingsContainer isDesktop={isDesktop}>
      <ScrollView
        ref={scrollViewRef}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 40 }}
      >
        <SettingsContentContainer isDesktop={isDesktop}>
          <SettingsHeaderSection isDesktop={isDesktop}>
            {!isDesktop && <SettingsTitle>Settings</SettingsTitle>}
            <SettingsSubHeader isDesktop={isDesktop}>
              Fine-tune your notification preferences to stay updated your way.
            </SettingsSubHeader>
          </SettingsHeaderSection>

          <LoginDetailsSection user={user} />
          <SectionDivider />

          {user?.client?.stripeCustomerId && (
            <>
              <PaymentSection />
              <SectionDivider />
            </>
          )}

          <NotificationsSection user={user} />
          <SectionDivider />

          <DeleteAccountSection onDeleteAccount={handleDeleteAccount} />

          {!isDesktop && (
            <BackToTopContainer>
              <BackToTopButton scrollToTop={scrollToTop} />
            </BackToTopContainer>
          )}
        </SettingsContentContainer>
      </ScrollView>

      <ConfirmDeleteAccountModal
        isVisible={isConfirmDeleteAccountModalOpen}
        setIsVisible={setIsConfirmDeleteAccountModalOpen}
      />
    </SettingsContainer>
  );
};

export default Settings;

const SettingsContainer = styled.View<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    flex: 1,
    backgroundColor: isDesktop ? 'white' : sand,
  }),
);

const SettingsContentContainer = styled.View<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    paddingHorizontal: isDesktop ? 53 : 25,
    paddingTop: isDesktop ? 0 : 20,
  }),
);

const SettingsHeaderSection = styled.View<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    marginBottom: 12,
    alignItems: isDesktop ? 'flex-start' : 'center',
  }),
);

const SettingsTitle = styled(H3)({
  textAlign: 'center',
  textTransform: 'uppercase',
  color: green,
  marginBottom: 8,
});

const SettingsSubHeader = styled(BodyText)<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    fontSize: 16,
    color: green,
    lineHeight: 22,
    textAlign: isDesktop ? 'left' : 'center',
    maxWidth: isDesktop ? 'auto' : 300,
  }),
);

const SectionDivider = styled.View({
  height: 1,
  backgroundColor: mintGreen,
});

const BackToTopContainer = styled.View({
  paddingTop: 75,
  paddingBottom: 24,
  alignItems: 'center',
});

const LoadingContainer = styled.View({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
});

const LoadingText = styled(BodyText)({
  marginTop: 16,
  fontSize: 16,
  color: green,
});
